import { useConfigurations } from "@via/api/configuration.tsx";
import UserGroupSelectComponent from "@via/components/select/user-group-select-component";
import { useAbility } from "@via/context/ability-context";
import EntityTagsSection from "@via/components/entity-tags-section.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { useChangesDetect } from "@via/hooks/useChangesDetect";
import ConfirmPermissionChangeModal from "@via/pages/projects/confirm-permission-change-modal.tsx";
import { getPermissionsEncoding } from "@via/pages/projects/runs/utils/helpers.tsx";
import { ROUTES, RunTabs, USER_ROLES } from "@via/utils/constants.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import {
  GetRunDetailsResponseBody,
  Permissions,
  PermissionType,
  TaggableEntityType,
} from "common";
import { useCallback, useEffect, useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";

const Settings = ({
  runSettingsData,
  form,
  activeRunTab,
}: {
  runSettingsData: GetRunDetailsResponseBody;
  form: UseFormReturn;
  activeRunTab: RunTabs;
}) => {
  const { runId = "" } = useParams();
  const { setValue, watch } = form;
  const { user } = useAuth();
  const isAdmin = user?.userRole === USER_ROLES.ADMIN;
  const { data: configurations } = useConfigurations();
  const groupId = watch("groupId");
  const { increaseChangeCount } = useChangesDetect();

  const ability = useAbility([
    {
      action: "Edit",
      subject: "Input",
      condition: runSettingsData?.owner?.own,
    },
    {
      // Only an admin may share a run publicly.
      action: "Submit",
      subject: "Button",
      condition: isAdmin,
    },
  ]);

  const defaultPermission =
    isAdmin && configurations?.ENABLE_SHARE_WITH_EVERYONE_RUN
      ? PermissionType.Public
      : PermissionType.UserOwned;

  const [permissions, setPermissions] =
    useState<PermissionType>(defaultPermission);

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingChange, setPendingChange] = useState<{
    prevType: PermissionType;
    prevGroupId?: number | null;
    prevGroupLabel?: string | null;

    nextType: PermissionType;
    nextValue: Permissions;
    nextGroupId?: number | null;
    nextGroupLabel?: string | null;
  } | null>(null);

  const currentPermissionType = useMemo(() => {
    // your existing helper already does this elsewhere:
    return (
      getPermissionsEncoding(runSettingsData?.permission as Permissions) ??
      PermissionType.UserOwned
    );
  }, [runSettingsData?.permission]);

  const currentGroupId = runSettingsData?.groupId ?? null;
  // If you have the current group label handy, pass it; otherwise null is fine.
  const currentGroupLabel: string | null = null;

  useEffect(() => {
    setValue("permission", runSettingsData?.permission);
    if (runSettingsData?.permission === Permissions.UserOwned) {
      setPermissions(PermissionType.UserOwned);
    } else if (runSettingsData?.permission === Permissions.Public) {
      setPermissions(PermissionType.Public);
    } else if (
      runSettingsData?.permission === Permissions.SpecificUsersShared
    ) {
      setPermissions(PermissionType.SpecificUsersShared);
    } else {
      setPermissions(PermissionType.GroupShared);
      setValue("groupId", runSettingsData?.groupId);
    }
  }, [runSettingsData, setValue]);

  const handleGroupIdUpdate = selected => {
    // When user picks a new group, stage the change based on *server* state
    setPendingChange({
      prevType: currentPermissionType,
      prevGroupId: currentGroupId,
      prevGroupLabel: currentGroupLabel,

      nextType: PermissionType.GroupShared,
      nextValue: Permissions.GroupShared,
      nextGroupId: selected.value,
      nextGroupLabel: selected?.label ?? null,
    });
    setConfirmModalOpen(true);
  };

  const handleGroupRadioSelect = () => {
    setPermissions(PermissionType.GroupShared);
    // The select can open already showing the run's stored group. Picking it again fires no
    // onChange, so the form never learned about it and Save kept the old permission. Stage the
    // shown group here, behind the same confirmation as picking one.
    const shownGroupId = groupId || runSettingsData?.groupId;
    if (!shownGroupId || watch("permission") === Permissions.GroupShared) {
      return;
    }
    setPendingChange({
      prevType: currentPermissionType,
      prevGroupId: currentGroupId,
      prevGroupLabel: currentGroupLabel,

      nextType: PermissionType.GroupShared,
      nextValue: Permissions.GroupShared,
      nextGroupId: shownGroupId,
      nextGroupLabel: null,
    });
    setConfirmModalOpen(true);
  };

  const handlePermissionUpdate = (type: PermissionType, value: Permissions) => {
    setPendingChange({
      prevType: currentPermissionType,
      prevGroupId: currentGroupId,
      prevGroupLabel: currentGroupLabel,

      nextType: type,
      nextValue: value,
      nextGroupId: null,
      nextGroupLabel: null,
    });
    setConfirmModalOpen(true);
  };

  const confirmPermissionChange = () => {
    if (!pendingChange) return;

    const { nextType, nextValue, nextGroupId } = pendingChange;
    increaseChangeCount();

    if (nextType !== PermissionType.GroupShared) {
      setValue("permission", nextValue);
      if (nextValue === Permissions.UserOwned) {
        setValue("groupId", null);
      } else {
        setValue("groupId", nextGroupId);
      }
    } else {
      setValue("permission", Permissions.GroupShared);
      setValue("groupId", nextGroupId);
    }

    // Reflect new UI state
    setPermissions(nextType);
    setConfirmModalOpen(false);
    setPendingChange(null);
  };

  const cancelPermissionChange = () => {
    if (pendingChange) {
      setPermissions(pendingChange.prevType);
    }
    setPendingChange(null);
    setConfirmModalOpen(false);
  };

  const getRunSettingsContext = useCallback(() => {
    return {
      run: {
        name: runSettingsData?.name,
        runUUID: runSettingsData?.runUUID,
        owner: runSettingsData?.owner?.username,
        own: runSettingsData?.owner?.own,
      },
      settings: {
        permissions: {
          type: permissions,
          permission: runSettingsData?.permission,
          groupId: runSettingsData?.groupId || groupId,
        },
      },
      user: {
        role: user?.userRole,
        canEdit: ability.can("Edit", "Input"),
        canSubmit: ability.can("Submit", "Button"),
      },
    };
  }, [runSettingsData, permissions, groupId, user?.userRole, ability]);

  useRegisterCortexContext({
    routePattern: ROUTES.RUN_DETAILS,
    contextName: "Run Settings",
    getContext: getRunSettingsContext,
    systemPrompt:
      "You are analyzing run settings page. Help answer questions about run settings and permissions.",
    enabled: !!runSettingsData && activeRunTab === RunTabs.Settings,
  });

  return (
    <div className="flex h-[74dvh] flex-row justify-start">
      <fieldset className="w-full">
        <label className="block pt-3 text-sm font-medium leading-6 text-gray-900">
          Set Permissions
        </label>
        <div className="mt-2 space-y-4">
          <div className="relative flex items-start">
            <div className="absolute flex h-6 items-center">
              <input
                id="permission-public"
                disabled={
                  !isAdmin ||
                  !ability.can("Edit", "Input") ||
                  !ability.can("Submit", "Button")
                }
                checked={permissions === PermissionType.Public}
                onChange={() =>
                  handlePermissionUpdate(
                    PermissionType.Public,
                    Permissions.Public
                  )
                }
                value={PermissionType.Public}
                aria-describedby="permission-public-description"
                type="radio"
                className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
              />
            </div>
            <div
              className={classNames(
                !isAdmin ||
                  !ability.can("Edit", "Input") ||
                  !ability.can("Submit", "Button")
                  ? "opacity-50"
                  : "",
                "pl-7 text-sm leading-6"
              )}>
              <label
                htmlFor="permission-public"
                className="font-medium text-gray-900">
                Public access
              </label>
              <p id="permission-public-description" className="text-gray-500">
                Everyone with the link will see this run.
                {!isAdmin && " Only administrators can share runs publicly."}
              </p>
            </div>
          </div>

          <div>
            <div className="relative flex items-start">
              <div className="absolute flex h-6 items-center">
                <input
                  id="permission-group"
                  disabled={!ability.can("Edit", "Input")}
                  checked={permissions === PermissionType.GroupShared}
                  onChange={handleGroupRadioSelect}
                  value={PermissionType.GroupShared}
                  aria-describedby="permission-group-description"
                  type="radio"
                  className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
              </div>
              <div
                className={classNames(
                  !ability.can("Edit", "Input") ? "opacity-50" : "",
                  "pl-7 text-sm leading-6"
                )}>
                <label
                  htmlFor="permission-group"
                  className="font-medium text-gray-900">
                  Share with a group
                </label>
                <p id="permission-group-description" className="text-gray-500">
                  Only users in the selected group would be able to access.
                </p>
              </div>
            </div>
          </div>
          {permissions === PermissionType.GroupShared && (
            <UserGroupSelectComponent
              name="groupId"
              isDisabled={!ability.can("Edit", "Input")}
              menuPlacement="top"
              {...(runSettingsData?.groupId
                ? {
                    missingId: groupId ? [groupId] : [runSettingsData.groupId],
                  }
                : [])}
              onChange={handleGroupIdUpdate}
            />
          )}
          {configurations?.ENABLE_SHARE_BUTTON && (
            <div>
              <div className="relative flex items-start">
                <div className="absolute flex h-6 items-center">
                  <input
                    id="permission-specific-users"
                    disabled={true}
                    checked={permissions === PermissionType.SpecificUsersShared}
                    value={PermissionType.SpecificUsersShared}
                    onChange={() =>
                      handlePermissionUpdate(
                        PermissionType.SpecificUsersShared,
                        Permissions.SpecificUsersShared
                      )
                    }
                    aria-describedby="permission-specific-users-description"
                    type="radio"
                    className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  />
                </div>
                <div
                  className={classNames(
                    !ability.can("Edit", "Input") ? "opacity-50" : "",
                    "pl-7 text-sm leading-6"
                  )}>
                  <label
                    htmlFor="permission-specific-users"
                    className="font-medium text-gray-900">
                    Share with specific users
                  </label>
                  <p
                    id="permission-specific-users-description"
                    className="text-gray-500">
                    Only users you share this project with would be able to
                    access.
                  </p>
                </div>
              </div>
            </div>
          )}
          <div>
            <div className="relative flex items-start">
              <div className="absolute flex h-6 items-center">
                <input
                  id="permission-private"
                  checked={permissions === PermissionType.UserOwned}
                  value={PermissionType.UserOwned}
                  onChange={() =>
                    handlePermissionUpdate(
                      PermissionType.UserOwned,
                      Permissions.UserOwned
                    )
                  }
                  aria-describedby="permission-private-description"
                  type="radio"
                  className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
              </div>
              <div
                className={classNames(
                  !ability.can("Edit", "Input") ? "opacity-50" : "",
                  "pl-7 text-sm leading-6"
                )}>
                <label
                  htmlFor="permission-private"
                  className="font-medium text-gray-900">
                  Private to you
                </label>
                <p
                  id="permission-private-description"
                  className="text-gray-500">
                  You are the only one able to access this run.
                </p>
              </div>
            </div>
          </div>
        </div>
        <EntityTagsSection
          entityType={TaggableEntityType.Run}
          entityId={runId}
          canEdit={ability.can("Edit", "Input")}
        />
      </fieldset>
      {confirmModalOpen && pendingChange && (
        <ConfirmPermissionChangeModal
          open={confirmModalOpen}
          setOpen={setConfirmModalOpen}
          currentType={pendingChange.prevType}
          currentGroupId={pendingChange.prevGroupId}
          currentGroupLabel={pendingChange.prevGroupLabel}
          nextType={pendingChange.nextType}
          nextGroupLabel={pendingChange.nextGroupLabel}
          nextGroupId={pendingChange.nextGroupId}
          onConfirm={confirmPermissionChange}
          onCancel={cancelPermissionChange}
          resource="run"
        />
      )}
    </div>
  );
};

export default Settings;
